import './MatchEditLigue.scss';

import { Edit } from '@mui/icons-material';
import { Alert, Button, Divider, Hidden, Pagination, TextField } from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import classNames from 'classnames';
import { IMatchRepository } from 'domain/event/match/Match.repository';
import { MatchGroup } from 'domain/event/match/MatchGroup';
import {
  MatchParticipant,
  UnpyMatchParticipantsFilters,
} from 'domain/event/match/MatchParticipant';
import { UnpyMatchFilters } from 'domain/event/match/UnpyMatch';
import { SystemPointsEnum } from 'domain/event/SystemPoints.enum';
import { UnpyEvent } from 'domain/event/UnpyEvent';
import { UnpyEventView } from 'domain/event/UnpyEventView';
import { Translate } from 'domain/translation/Translation.repository';
import { insertIf } from 'helper/array.helper';
import { formatDate } from 'helper/date.helper';
import { toEnum } from 'helper/enum.helper';
import { EventMatchsParametersProps } from 'primary/classements/MatchsParametersByEventType';
import { CoupleButtonValidCancel } from 'primary/Components/CoupleButtonValidCancel/CoupleButtonValidCancel';
import { InputWrapper } from 'primary/Components/Input/InputWrapper';
import { SkeletonDatagrid } from 'primary/Components/SkeletonNoContent/SkeletonDatagrid';
import { sendEventToastMessage } from 'primary/Components/Toast/Toast.helper';
import { ParticipantListByMatchsMobile } from 'primary/events/components/matchs/edit/mobile/ParticipantListByMatchsMobile';
import { ModalEditDetailsMatchBR } from 'primary/events/components/matchs/edit/ModalEditDetailsMatchBR';
import { HeaderMatchsEdit } from 'primary/events/components/matchs/HeaderMatchsEdit';
import { useBooleanToggle } from 'primary/hooks/useBooleanToggle';
import { useContextDependency } from 'primary/hooks/useContextDependency';
import { useFetchWithFilterPage } from 'primary/hooks/useFetchWithFilterPage';
import { useTranslate } from 'primary/hooks/useTranslate';
import { useYupValidationResolver } from 'primary/hooks/useYupValidationResolver';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';

export const MatchEditLigueBR = ({ event, retry }: EventMatchsParametersProps) => {
  const t = useTranslate();
  return (
    <div className={'matchEditLigue'}>
      <Hidden smUp>
        <ParticipantListByMatchsMobile
          systemPoints={toEnum(SystemPointsEnum, event.pointSystem)}
          eventId={event.id}
        />
      </Hidden>
      <Hidden smDown>
        <ParticipantListByMatchBR
          systemPoint={toEnum(SystemPointsEnum, event.pointSystem)}
          event={event}
        />
      </Hidden>
    </div>
  );
};

type MatchListViewByGroupProps = {
  event: UnpyEvent | UnpyEventView;
  systemPoint: SystemPointsEnum;
};
export const ParticipantListByMatchBR = ({
  event,
  systemPoint,
}: MatchListViewByGroupProps) => {
  const t = useTranslate();
  const { matchsRepository } = useContextDependency();
  const [selectedGroup, setSelectedGroup] = React.useState<MatchGroup | null>(null);
  const {
    setFilters,
    activeFilters,
    fetchedResource,
    nextPage,
    loading: loadingPage,
    goToPage,
    retry,
    addToCurrentPage,
  } = useFetchWithFilterPage<MatchGroup, UnpyMatchFilters>({
    keyStoreFilters: 'groups-ligue-filters',
    staticFilters: {
      idEvent: event.id,
      withMatchs: true,
    },
    filters: {},
    callApi: (filters, page) => matchsRepository.searchGroup(filters, page, 10),
  });

  return (
    <div className={'matchListViewByGroup'}>
      <div className={'matchListViewByGroup__groupList'}>
        <div className={'matchListViewByGroup__groupList-content --scrollBarContent'}>
          <h5>Liste des matchs :</h5>
          <Divider />

          {!fetchedResource?.content?.length && <p>{t('event.matchs.noGroup')}</p>}
          {fetchedResource?.content &&
            fetchedResource?.content.map((group) => (
              <div
                key={group.id}
                className={classNames('-item', {
                  ['-selected']: group.id === selectedGroup?.id,
                })}
                onClick={() => setSelectedGroup(group)}
              >
                <p>{group.title}</p>
                <span>
                  {t('event.matchs.numberMatchs', {
                    count: group?.totalMatchsInGroup ?? 0,
                  })}
                </span>
              </div>
            ))}
        </div>
        <div className={'matchListViewByGroup__groupList-pagination'}>
          <Pagination
            key={fetchedResource?.currentPage}
            count={fetchedResource?.pagesCount || 1}
            page={fetchedResource?.currentPage ? fetchedResource?.currentPage + 1 : 1}
            size={'medium'}
            onChange={(e, value) => goToPage(value - 1)}
          />
        </div>
      </div>
      <div className={'matchListViewByGroup__matchList'}>
        {!selectedGroup && (
          <p>
            {
              //@ts-ignore
              t(`event.matchs.selectGroup.${event.typeEvent.code}`)
            }
          </p>
        )}
        {selectedGroup && (
          <ParticipantListOfMatch
            key={selectedGroup.id}
            systemPoint={systemPoint}
            group={selectedGroup}
          />
        )}
      </div>
    </div>
  );
};
type ScoreUpdateValidationFormData = {
  idMatchParticipant: number;
  score?: number;
};

export type MatchUpdateFormData = {
  id: number;
  startDate?: string;
  scoreUpdateValidations: ScoreUpdateValidationFormData[];
};

const formatMatchParticipantToRowData = (
  matchParticipants: MatchParticipant[],
  t: Translate,
) => {
  return matchParticipants.map((matchParticipant, index) => ({
    id: matchParticipant.id,
    name: matchParticipant.eventParticipant.team.name,
    position: {
      matchParticipant: matchParticipant,
      position: matchParticipant.score ? index + 1 : 'N/A',
    },
    points: matchParticipant,
  }));
};

const useOnSubmit =
  (
    matchRepository: IMatchRepository,
    onSubmitted: () => void,
    setLoadingState: (loadingState: boolean) => void,
  ) =>
  (data: MatchUpdateFormData) => {
    setLoadingState(true);
    matchRepository
      .update({
        ...data,
        scoreUpdateValidations: Object.values(data.scoreUpdateValidations).filter(
          (s) => !!s.idMatchParticipant,
        ),
      })
      .then(() => {
        onSubmitted();
        sendEventToastMessage('event.matchs.edit.success', 'success');
      })
      .finally(() => {
        setLoadingState(false);
      });
  };

type ParticipantListOfMatchProps = {
  group: MatchGroup;
  systemPoint: SystemPointsEnum;
};
const ParticipantListOfMatch = ({ group, systemPoint }: ParticipantListOfMatchProps) => {
  const { matchsRepository } = useContextDependency();

  const {
    setFilters,
    activeFilters,
    fetchedResource,
    nextPage,
    retry,
    goToPage,
    loading: loadingPage,
    addToCurrentPage,
  } = useFetchWithFilterPage<MatchParticipant, UnpyMatchParticipantsFilters>({
    keyStoreFilters: 'matchs-ligue-br-filters',
    staticFilters: {
      matchId: group.matchs[0].id,
    },
    filters: {},
    callApi: (filters, page, nbPerPage) =>
      matchsRepository.searchParticipants(filters, page, nbPerPage),
  });
  const sortedParticipants = group.matchs[0].participants.sort(
    (a, b) => b.score - a.score,
  );
  const t = useTranslate();
  const validationSchema = yup.object({
    id: yup.number().required(t('general.forms.errors.required')),
    startDate: yup.string().nullable(),
    scoreUpdateValidations: yup.object().shape(
      sortedParticipants.reduce((acc, p) => {
        return {
          ...acc,
          [p.id]: yup
            .object({
              idMatchParticipant: yup.number().nullable(),
              score:
                systemPoint === SystemPointsEnum.DEFAULT
                  ? yup
                      .number()
                      .transform((value) => (isNaN(value) ? undefined : value))
                      .nullable()
                      .min(0, t('event.matchs.edit.editScore.brFieldMinError'))
                      .max(
                        group.matchs[0].participants.length,
                        t('event.matchs.edit.editScore.brFieldMaxError'),
                      )
                  : yup.number().nullable(),
            })
            .nullable(),
        };
      }, {}),
    ),
  });
  const resolver = useYupValidationResolver(validationSchema);
  const {
    handleSubmit,
    control,
    setValue,
    trigger,
    register,
    getValues,
    watch,
    formState: { isSubmitting, errors },
  } = useForm<MatchUpdateFormData>({
    resolver,
    defaultValues: {
      id: group.matchs[0].id,
      startDate: group.matchs[0].startDate
        ? formatDate(group.matchs[0].startDate, 'inputDatetime')
        : undefined,
      // @ts-ignore
      scoreUpdateValidations: sortedParticipants.reduce((acc, p, index) => {
        return {
          ...acc,
          [p.id]: {
            idMatchParticipant: p.id,
            score:
              systemPoint === SystemPointsEnum.DEFAULT
                ? !p.score
                  ? null
                  : group.matchs[0].participants.length - p.score
                : p.score,
          },
        };
      }, {}),
    },
  });
  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: t('event.teams.datatable.labelTeam'),
      flex: 2,
    },
    // @ts-ignore
    ...insertIf(systemPoint === SystemPointsEnum.DEFAULT, {
      field: 'position',
      headerName: t('event.teams.datatable.labelPosition'),
      flex: 1,
      headerAlign: 'center',
      sortable: false,
      cellClassName: '-cellScore',
      renderCell: (
        value: GridRenderCellParams<{
          matchParticipant: MatchParticipant;
          position: string;
        }>,
      ) => {
        if (!value.value) return;
        return (
          <InputWrapper
            errors={errors}
            Input={TextField}
            className={'-inputScoreInTable'}
            inputProps={{
              type: 'number',
              variant: 'filled',
              ...register(
                `scoreUpdateValidations.${value.value.matchParticipant.id}.score`,
              ),
              min: 0,
              max: group.matchs[0].participants.length,
            }}
          />
        );
      },
    }),
    // @ts-ignore
    ...insertIf(systemPoint === SystemPointsEnum.PERSO, {
      field: 'points',
      headerName: t('event.teams.datatable.labelPoints'),
      flex: 1,
      headerAlign: 'center',
      sortable: false,
      cellClassName: '-cellScore',
      renderCell: (value: GridRenderCellParams<MatchParticipant>) => {
        if (!value.value) return;
        return (
          <InputWrapper
            errors={errors}
            Input={TextField}
            className={'-inputScoreInTable'}
            inputProps={{
              type: 'number',
              variant: 'filled',
              ...register(`scoreUpdateValidations.${value.value.id}.score`),
              min: 0,
              max: group.matchs[0].participants.length,
            }}
          />
        );
      },
    }),
  ];
  const [loading, setLoading] = useState(false);
  const [isOpenEditScore, , openEditScore, closeEditScore] = useBooleanToggle();
  const onSubmit = useOnSubmit(matchsRepository, retry, setLoading);
  const setFilterByKey = (
    key: keyof UnpyMatchFilters & UnpyMatchParticipantsFilters,
    val?: string | number | boolean,
  ) => {
    setFilters({ ...(activeFilters?.filters ?? {}), [key]: val });
  };
  return (
    <div className={'matchListOfGroup'}>
      <div className={'matchListOfGroup__header'}>
        <div className={'-titleWithDetails'}>
          <h4>{t('event.matchs.participantMatchTitle')}</h4>
          <Button variant={'contained'} color={'primary'} onClick={openEditScore}>
            <Edit />
            {t('event.matchs.openEditScoreBtn')}
          </Button>
        </div>
        <Alert severity="info">
          {t(`event.matchs.edit.editScore.modal.noticeBR.${systemPoint}`)}
        </Alert>
      </div>
      <HeaderMatchsEdit
        retry={retry}
        withTextField
        event={group.event}
        filters={activeFilters?.filters ?? {}}
        setFilters={setFilterByKey}
      />

      <div className={'matchListOfGroup-content --scrollBarContent'}>
        <Divider />
        {!loadingPage && !fetchedResource?.content?.length && (
          <p>{t('event.matchs.noMatchsInGroup')}</p>
        )}
        <form onSubmit={handleSubmit(onSubmit)} className={'-formContainer'}>
          <div className={'matchListOfGroup-pagination'}>
            <Pagination
              key={fetchedResource?.currentPage}
              count={fetchedResource?.pagesCount || 1}
              page={fetchedResource?.currentPage ? fetchedResource?.currentPage + 1 : 1}
              size={'medium'}
              onChange={(e, value) => goToPage(value - 1)}
            />
          </div>
          {loadingPage && <SkeletonDatagrid rowNumber={10} />}
          {!loadingPage && fetchedResource?.content && (
            <DataGrid
              disableColumnFilter
              disableColumnMenu
              disableColumnSelector
              disableDensitySelector
              disableEval
              disableVirtualization
              className={'-datatable'}
              sx={{ color: 'white' }}
              isRowSelectable={() => false}
              disableRowSelectionOnClick={true}
              hideFooter
              rows={formatMatchParticipantToRowData(fetchedResource.content, t)}
              columns={columns}
            />
          )}
          <CoupleButtonValidCancel
            isTypeSubmit={true}
            disabledValid={loading}
            loading={loading}
          />
        </form>
      </div>
      <ModalEditDetailsMatchBR
        systemPoint={group.event.pointSystem as SystemPointsEnum}
        isOpen={isOpenEditScore}
        onValidEdit={() => {
          closeEditScore();
          retry();
        }}
        handleClose={closeEditScore}
        retry={retry}
        match={group.matchs[0]}
      />
    </div>
  );
};
